import type { MarketId } from '@ps/isp-web-services-types';
import { noop, pick } from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { MarketPollingContext } from '../providers/MarketPollingProvider';
import type { MarketPollingData } from '../types/market';

const useMarketPolling = (
  marketIds: MarketId[],
  callback: (data: MarketPollingData) => void = noop
): void => {
  const subject = useRef(new Subject<MarketPollingData>());
  const { add, remove, marketsObservable } = useContext(MarketPollingContext);

  useEffect(() => {
    if (marketIds.length > 0) {
      add(marketIds);

      return () => {
        remove(marketIds);
      };
    }
  }, [marketIds, add, remove]);

  useEffect(() => {
    const observable = marketsObservable
      .pipe(
        map(({ data, closed }) => ({
          data: pick(data, marketIds),
          closed: marketIds.filter((id) => closed.includes(id))
        }))
      )
      .subscribe((data) => {
        subject.current.next(data);
      });

    // We're using a cold observable, that way we don't keep track of the previous values when initially subscribed
    const subscription = subject.current.subscribe(callback);

    return () => {
      subscription.unsubscribe();
      observable.unsubscribe();
    };
  }, [callback, marketIds, marketsObservable]);
};

export default useMarketPolling;
